import Api from "./rhinoApi";
import { createEmptyRhinoCard } from "../rhinoObjects";
import { combineNationalDataForBarCharts, calculateTopEightBarCharts, combineFilters } from "./modulesCommon";
import { combineAndCommit, clone } from "../helpers";

// TYPES
const DATA = "DATA";
const PATIENT_COMORBIDITIES = "PATIENT_COMORBIDITIES";
const DIAGNOSES_MAIN_QUESTION = "DIAGNOSES_MAIN_QUESTION";
const DIAGNOSES_SUB_QUESTION = "DIAGNOSES_SUB_QUESTION";
const SURGERY_TYPE = "SURGERY_TYPE";
const NASAL_PACKAGING = "NASAL_PACKAGING";
const IMAGE_GUIDED_SURGERY = "IMAGE_GUIDED_SURGERY";
const COMPLICATIONS = "COMPLICATIONS";
const EDEMA = "EDEMA";
const SECRETION = "SECRETION";
const POLYP_SIZE = "POLYP_SIZE";
const PROCEDURES = "PROCEDURES";
const HOSPITAL_STAY = "HOSPITAL_STAY";
const BACK_TO_NORMAL_ACTIVITY = "BACK_TO_NORMAL_ACTIVITY";
const UNPLANNED_VISIT_MAIN_QUESTION = "UNPLANNED_VISIT_MAIN_QUESTION";
const UNPLANNED_VISIT_SUB_QUESTION = "UNPLANNED_VISIT_SUB_QUESTION";
const PROBLEMS_AFTER_SURGERY_MAIN_QUESTION = "PROBLEMS_AFTER_SURGERY_MAIN_QUESTION";
const PROBLEMS_AFTER_SURGERY_SUB_QUESTION = "PROBLEMS_AFTER_SURGERY_SUB_QUESTION";
const SMOKING_PREOP = "SMOKING_PREOP";
const SMOKING_THREE_MONTH_POSTOP = "SMOKING_THREE_MONTH_POSTOP";
const SMOKING_TWELVE_MONTH_POSTOP = "SMOKING_TWELVE_MONTH_POSTOP";
const NOSE_AND_SINUS_FUNCTION_SCORE_THREE_MONTH_POSTOP = "NOSE_AND_SINUS_FUNCTION_SCORE_THREE_MONTH_POSTOP";
const NOSE_AND_SINUS_FUNCTION_SCORE_TWELVE_MONTH_POSTOP = "NOSE_AND_SINUS_FUNCTION_SCORE_TWELVE_MONTH_POSTOP";
const LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_MAIN_QUESTION =
    "LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_MAIN_QUESTION";
const LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_SUB_QUESTION =
    "LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_SUB_QUESTION";
const LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_MAIN_QUESTION =
    "LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_MAIN_QUESTION";
const LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_SUB_QUESTION =
    "LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_SUB_QUESTION";
const LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_SCORES = "LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_SCORES";
const LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_SCORES = "LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_SCORES";
const SNOT_MOST_IMPORTANT_ITEMS = "SNOT_MOST_IMPORTANT_ITEMS";
const DATA_LOADED = "DATA_LOADED";
const DATA_LOADING = "DATA_LOADING";
const NATIONAL_DATA = "NATIONAL_DATA";
const NATIONAL_DATA_LOADED = "NATIONAL_DATA_LOADED";
const MR_ATTRIBUTES = "MR_ATTRIBUTES";

const emptyDataObj = {
    patientDemographics: {},
    patientComorbidities: {},
    caseStatistics: {},
    diagnoses: {
        mainQuestion: {},
        subQuestion: {},
    },
    questionnairesStatistics: {
        scores: {
            nose: Object.assign({}, createEmptyRhinoCard()),
            snot_22: Object.assign({}, createEmptyRhinoCard()),
            vas: Object.assign({}, createEmptyRhinoCard()),
        },
        snotMostImportantItems: {},
    },
    followUpStatistics: {
        hospitalStay: {},
        backToNormalActivity: {},
        unplannedVisit: {
            mainQuestion: {},
            subQuestion: {},
        },
        problemsAfterSurgery: {
            mainQuestion: {},
            subQuestion: {},
        },
    },
    compositeSurgeryStatistics: {
        imageGuidedSurgery: {},
        nasalPacking: {},
        complications: {},
        surgeryStatistics: {
            surgeryType: {},
        },
        labResultsStatistics: {},
        symptomsStatistics: {
            polypSize: {},
            edema: {},
            secretion: {},
        },
        procedures: {},
    },
    vasStatusStatistics: {
        smoking: {
            preop: {},
            threeMonthPostOp: {},
            twelveMonthPostOp: {},
        },
        noseSinusFunction: {
            threeMoPostOp: {
                score: {},
            },
            twelveMoPostOp: {
                score: {},
            },
        },
        lungLowerAirwaysProblems: {
            threeMoPostOp: {
                problems: {
                    mainQuestion: {},
                    subQuestion: {},
                },
            },
            twelveMoPostOp: {
                problems: {
                    mainQuestion: {},
                    subQuestion: {},
                },
            },
        },
    },
};

const fields = [
    {
        commitTo: PATIENT_COMORBIDITIES,
        path: ["patientComorbidities"],
        calculateTopEight: true,
    },
    {
        commitTo: DIAGNOSES_MAIN_QUESTION,
        path: ["diagnoses", "mainQuestion"],
    },
    {
        commitTo: DIAGNOSES_SUB_QUESTION,
        path: ["diagnoses", "subQuestion"],
    },
    {
        commitTo: SURGERY_TYPE,
        path: ["compositeSurgeryStatistics", "surgeryStatistics", "surgeryType"],
    },
    {
        commitTo: NASAL_PACKAGING,
        path: ["compositeSurgeryStatistics", "nasalPacking"],
    },
    {
        commitTo: IMAGE_GUIDED_SURGERY,
        path: ["compositeSurgeryStatistics", "imageGuidedSurgery"],
    },
    {
        commitTo: COMPLICATIONS,
        path: ["compositeSurgeryStatistics", "complications"],
    },
    {
        commitTo: POLYP_SIZE,
        path: ["compositeSurgeryStatistics", "symptomsStatistics", "polypSize"],
    },
    {
        commitTo: EDEMA,
        path: ["compositeSurgeryStatistics", "symptomsStatistics", "edema"],
    },
    {
        commitTo: SECRETION,
        path: ["compositeSurgeryStatistics", "symptomsStatistics", "secretion"],
    },
    {
        commitTo: PROCEDURES,
        path: ["compositeSurgeryStatistics", "procedures"],
        calculateTopEight: true,
    },
    {
        commitTo: HOSPITAL_STAY,
        path: ["followUpStatistics", "hospitalStay"],
    },
    {
        commitTo: BACK_TO_NORMAL_ACTIVITY,
        path: ["followUpStatistics", "backToNormalActivity"],
    },
    {
        commitTo: UNPLANNED_VISIT_MAIN_QUESTION,
        path: ["followUpStatistics", "unplannedVisit", "mainQuestion"],
    },
    {
        commitTo: UNPLANNED_VISIT_SUB_QUESTION,
        path: ["followUpStatistics", "unplannedVisit", "subQuestion"],
    },
    {
        commitTo: PROBLEMS_AFTER_SURGERY_MAIN_QUESTION,
        path: ["followUpStatistics", "problemsAfterSurgery", "mainQuestion"],
    },
    {
        commitTo: PROBLEMS_AFTER_SURGERY_SUB_QUESTION,
        path: ["followUpStatistics", "problemsAfterSurgery", "subQuestion"],
    },
    {
        commitTo: SMOKING_PREOP,
        path: ["vasStatusStatistics", "smoking", "preop"],
    },
    {
        commitTo: SMOKING_THREE_MONTH_POSTOP,
        path: ["vasStatusStatistics", "smoking", "threeMonthPostOp"],
    },
    {
        commitTo: SMOKING_TWELVE_MONTH_POSTOP,
        path: ["vasStatusStatistics", "smoking", "twelveMonthPostOp"],
    },
    {
        commitTo: NOSE_AND_SINUS_FUNCTION_SCORE_THREE_MONTH_POSTOP,
        path: ["vasStatusStatistics", "noseSinusFunction", "threeMoPostOp", "score"],
    },
    {
        commitTo: NOSE_AND_SINUS_FUNCTION_SCORE_TWELVE_MONTH_POSTOP,
        path: ["vasStatusStatistics", "noseSinusFunction", "twelveMoPostOp", "score"],
    },
    {
        commitTo: LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_MAIN_QUESTION,
        path: ["vasStatusStatistics", "lungLowerAirwaysProblems", "threeMoPostOp", "problems", "mainQuestion"],
    },
    {
        commitTo: LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_SUB_QUESTION,
        path: ["vasStatusStatistics", "lungLowerAirwaysProblems", "threeMoPostOp", "problems", "subQuestion"],
    },

    {
        commitTo: LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_MAIN_QUESTION,
        path: ["vasStatusStatistics", "lungLowerAirwaysProblems", "twelveMoPostOp", "problems", "mainQuestion"],
    },
    {
        commitTo: LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_SUB_QUESTION,
        path: ["vasStatusStatistics", "lungLowerAirwaysProblems", "twelveMoPostOp", "problems", "subQuestion"],
    },

    {
        commitTo: SNOT_MOST_IMPORTANT_ITEMS,
        path: ["questionnairesStatistics", "snotMostImportantItems"],
        calculateTopEight: true,
    },
];

const rhinoDataModule = {
    namespaced: true,
    state: () => ({
        mrAttributes: Object.create({}),
        data: clone(emptyDataObj),
        dataLoaded: null,
        dataLoading: false,
        nationalData: clone(emptyDataObj),
        nationalDataLoaded: false,
    }),
    mutations: {
        [MR_ATTRIBUTES](state, mrAttributes) {
            state.mrAttributes = mrAttributes;
        },
        [DATA](state, data) {
            state.data = data;
        },
        [PATIENT_COMORBIDITIES](state, obj) {
            state.data.patientComorbidities = obj;
        },
        [DIAGNOSES_MAIN_QUESTION](state, obj) {
            state.data.diagnoses.mainQuestion = obj;
        },
        [DIAGNOSES_SUB_QUESTION](state, obj) {
            state.data.diagnoses.subQuestion = obj;
        },
        [SURGERY_TYPE](state, obj) {
            state.data.compositeSurgeryStatistics.surgeryStatistics.surgeryType = obj;
        },
        [NASAL_PACKAGING](state, obj) {
            state.data.compositeSurgeryStatistics.nasalPacking = obj;
        },
        [IMAGE_GUIDED_SURGERY](state, obj) {
            state.data.compositeSurgeryStatistics.imageGuidedSurgery = obj;
        },
        [COMPLICATIONS](state, obj) {
            state.data.compositeSurgeryStatistics.complications = obj;
        },
        [SECRETION](state, obj) {
            state.data.compositeSurgeryStatistics.symptomsStatistics.secretion = obj;
        },
        [POLYP_SIZE](state, obj) {
            state.data.compositeSurgeryStatistics.symptomsStatistics.polypSize = obj;
        },
        [EDEMA](state, obj) {
            state.data.compositeSurgeryStatistics.symptomsStatistics.edema = obj;
        },
        [PROCEDURES](state, obj) {
            state.data.compositeSurgeryStatistics.procedures = obj;
        },
        [HOSPITAL_STAY](state, obj) {
            state.data.followUpStatistics.hospitalStay = obj;
        },
        [BACK_TO_NORMAL_ACTIVITY](state, obj) {
            state.data.followUpStatistics.backToNormalActivity = obj;
        },
        [UNPLANNED_VISIT_MAIN_QUESTION](state, obj) {
            state.data.followUpStatistics.unplannedVisit.mainQuestion = obj;
        },
        [UNPLANNED_VISIT_SUB_QUESTION](state, obj) {
            state.data.followUpStatistics.unplannedVisit.subQuestion = obj;
        },
        [PROBLEMS_AFTER_SURGERY_MAIN_QUESTION](state, obj) {
            state.data.followUpStatistics.problemsAfterSurgery.mainQuestion = obj;
        },
        [PROBLEMS_AFTER_SURGERY_SUB_QUESTION](state, obj) {
            state.data.followUpStatistics.problemsAfterSurgery.subQuestion = obj;
        },
        [SMOKING_PREOP](state, obj) {
            state.data.vasStatusStatistics.smoking.preop = obj;
        },
        [SMOKING_THREE_MONTH_POSTOP](state, obj) {
            state.data.vasStatusStatistics.smoking.threeMonthPostOp = obj;
        },
        [SMOKING_TWELVE_MONTH_POSTOP](state, obj) {
            state.data.vasStatusStatistics.smoking.twelveMonthPostOp = obj;
        },
        [NOSE_AND_SINUS_FUNCTION_SCORE_THREE_MONTH_POSTOP](state, obj) {
            state.data.vasStatusStatistics.noseSinusFunction.threeMoPostOp.score = obj;
        },
        [NOSE_AND_SINUS_FUNCTION_SCORE_TWELVE_MONTH_POSTOP](state, obj) {
            state.data.vasStatusStatistics.noseSinusFunction.twelveMoPostOp.score = obj;
        },
        [LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_MAIN_QUESTION](state, obj) {
            state.data.vasStatusStatistics.lungLowerAirwaysProblems.threeMoPostOp.problems.mainQuestion = obj;
        },
        [LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_SUB_QUESTION](state, obj) {
            state.data.vasStatusStatistics.lungLowerAirwaysProblems.threeMoPostOp.problems.subQuestion = obj;
        },
        [LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_MAIN_QUESTION](state, obj) {
            state.data.vasStatusStatistics.lungLowerAirwaysProblems.twelveMoPostOp.problems.mainQuestion = obj;
        },
        [LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_SUB_QUESTION](state, obj) {
            state.data.vasStatusStatistics.lungLowerAirwaysProblems.twelveMoPostOp.problems.subQuestion = obj;
        },
        [LONG_LOWER_AIRWAYS_PROBLEMS_THREE_MONTH_POSTOP_SCORES](state, obj) {
            state.data.vasStatusStatistics.lungLowerAirwaysProblems.threeMoPostOp.problemsScores = obj;
        },
        [LONG_LOWER_AIRWAYS_PROBLEMS_TWELVE_MONTH_POSTOP_SCORES](state, obj) {
            state.data.vasStatusStatistics.lungLowerAirwaysProblems.twelveMoPostOp.problemsScores = obj;
        },

        [SNOT_MOST_IMPORTANT_ITEMS](state, obj) {
            state.data.questionnairesStatistics.snotMostImportantItems = obj;
        },
        [DATA_LOADED](state, isDataLoaded) {
            state.dataLoaded = isDataLoaded;
        },
        [DATA_LOADING](state, dataLoading) {
            state.dataLoading = dataLoading;
        },
        [NATIONAL_DATA](state, obj) {
            state.nationalData = obj;
        },
        [NATIONAL_DATA_LOADED](state, isNationalDataLoaded) {
            state.nationalDataLoaded = isNationalDataLoaded;
        },
    },
    actions: {
        readData({ commit, state, rootState }, context) {
            let filters = combineFilters(rootState.patientQuery, rootState.caseQuery, rootState.advancedQueryCriteria);
            commit(DATA_LOADED, false);

            return Api.GetData(context, filters).then(function (result) {
                calculateTopEightBarCharts(fields, result.data);

                // if context data should be combined with national data
                if (rootState.dataModule.system.isNationalDataNeeded) {
                    combineNationalDataForBarCharts(fields, result.data, state.nationalData);
                }
                commit(DATA, result.data);
                commit(DATA_LOADED, true);
            });
        },
        readNationalData({ commit, state, rootState }) {
            commit(NATIONAL_DATA_LOADED, false);
            let filters = combineFilters(rootState.patientQuery, rootState.caseQuery, rootState.advancedQueryCriteria);
            return Api.GetNationalData(filters, rootState).then(function (result) {
                fields.forEach((element) => {
                    let statePath = state.data;
                    let resultPath = result.data;

                    for (let index = 0; index < element.path.length; index++) {
                        statePath = statePath[element.path[index]];
                        resultPath = resultPath[element.path[index]];
                    }

                    combineAndCommit(commit, element.commitTo, statePath, resultPath);
                });

                commit(NATIONAL_DATA, result.data);
                commit(NATIONAL_DATA_LOADED, true);
            });
        },
        // is data was loaded
        setDataLoaded({ commit }, isDataLoaded) {
            return commit(DATA_LOADED, isDataLoaded);
        },
        setDataLoading({ commit }, isDataLoading) {
            return commit(DATA_LOADING, isDataLoading);
        },
        cleanData({ commit }) {
            commit(DATA, clone(emptyDataObj));
            commit(DATA_LOADED, false);
        },
        readMrAttributes({ commit }) {
            return Api.GetMrAttributes().then((result) => {
                commit(MR_ATTRIBUTES, result.data);
            });
        },
    },
};

export default rhinoDataModule;
